import { useContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { CheckboxTreeFilterable } from 'components/Form/CheckboxTreeFilterable'
import { SectionHeader } from 'components/Header'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import {
  AbTestsAddEditContext,
  AbTestsAddEditContextType,
} from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { useGetAbTestParametersSchema } from 'features/ab-tests/hooks/useGetAbTestParametersSchema'
import { AbTestParametersSchema } from 'features/ab-tests/types/abTestParametersSchema'
import { generateSelector } from 'providers/product-fruits/utils'
import { AbTestsCommonFormProps } from '../types'
import { checkParameters } from '../utils'

import type { AbTestsCommonSchema, VariantKey, VariantsSchema } from '../abTestsCommonSchema'
import type { Options } from 'components/Form/CheckboxTreeFilterable'

export const ParametersSection = ({ variant }: AbTestsCommonFormProps): React.JSX.Element => {
  const { t } = useTranslation('features/abTests', { keyPrefix: 'common' })

  const { control, setValue } = useFormContext<AbTestsCommonSchema>()
  const { domainId, parameters } = useWatch()

  const { loading, schema } = useGetAbTestParametersSchema({ domainId })
  const [parametersArePreChecked, setParametersArePreChecked] = useState<boolean>(false)

  const {
    abTest,
    isDisabled,
    loading: formLoading,
  } = useContext<AbTestsAddEditContextType>(AbTestsAddEditContext)

  useEffect(() => {
    if (schema !== undefined) {
      setValue('parameters', schema)
      setParametersArePreChecked(false)
    }
  }, [schema])

  useEffect(() => {
    if (
      variant === 'edit' &&
      parameters !== undefined &&
      parametersArePreChecked === false &&
      abTest !== undefined
    ) {
      const firstVariantKey = Object.keys(abTest.variants)[0] as VariantKey
      const preCheckedParameters: AbTestParametersSchema = checkParameters({
        parameters,
        variant: abTest.variants[firstVariantKey] as VariantsSchema,
      })

      setValue('parameters', preCheckedParameters)
      setParametersArePreChecked(true)
    }
  }, [parameters, parametersArePreChecked])

  if (loading) {
    return <Loading />
  } else if (schema === undefined) {
    return <></>
  }

  return (
    <Paper
      data-product-fruits={
        variant === 'add' ? generateSelector({ step: 3, subTour: 2, tour: 5 }) : undefined
      }
    >
      <SectionHeader
        subtitle={t('parameters.subtitle')}
        title={t('parameters.title')}
      />

      <GapBox />

      <CheckboxTreeFilterable<AbTestsCommonSchema>
        control={control}
        disabled={isDisabled || formLoading}
        name='parameters'
        options={schema as unknown as Options}
        placeholder={t('parameters.search')}
      />
    </Paper>
  )
}
